import React from 'react';
import { Box, Typography, LinearProgress, IconButton } from '@mui/material';
import { Close as CloseIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import { useUpload } from 'src/contexts/UploadContext';

/**
 * Global upload indicator that shows active uploads
 * Persists across navigation and conversation changes
 */
const GlobalUploadIndicator = () => {
  const { activeUploads, cancelUpload } = useUpload();

  if (activeUploads.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 80,
        right: 20,
        zIndex: 9999,
        maxWidth: 320,
        width: '100%',
      }}
    >
      {activeUploads.map((upload) => (
        <Box
          key={upload.id}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: 2,
            marginBottom: 1,
            border: '1px solid #E5E7EB',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UploadIcon sx={{ color: '#7C3AED', fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#1F2937',
                  fontSize: '14px',
                }}
              >
                Uploading {upload.files?.length || 0} file{upload.files?.length > 1 ? 's' : ''}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => cancelUpload(upload.id)}
              sx={{
                width: 24,
                height: 24,
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                  color: '#1F2937',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <LinearProgress
            variant="determinate"
            value={upload.overallProgress || 0}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#7C3AED',
                borderRadius: 3,
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: '#6B7280',
              fontSize: '12px',
              marginTop: 0.5,
              display: 'block',
            }}
          >
            {upload.overallProgress || 0}% complete
          </Typography>

          {upload.status === 'failed' && (
            <Typography
              variant="caption"
              sx={{
                color: '#EF4444',
                fontSize: '12px',
                marginTop: 0.5,
                display: 'block',
              }}
            >
              Upload failed. Please try again.
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default GlobalUploadIndicator;
